import { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpRight, Plus, Rocket, Send, X, Zap } from "lucide-react";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  GET_PRIMARY_TASK_PROGRESS,
  GET_SHOP_DATA_FOR_RECOMMENDATION,
  GET_USER_ACCOUNT_TOKENS,
} from "../../graphql/queries";

import { getShopDataReadiness } from "../../utils/api/shopApi";
import ZeqiGreetingSection from "../../components/home/ZeqiGreetingSection";
import ZeqiStrategyReportSection from "../../components/home/ZeqiStrategyReportSection";
import ZeqiKeyFocusAreaSection from "../../components/home/ZeqiKeyFocusAreaSection";
import ChannelWorkspace from "../../components/home/ChannelWorkspace";
import { getChannelWorkspaceHeaderActions } from "../../components/home/channelWorkspaceConfig";
import ZeqiLoadingModal from "../../components/home/ZeqiLoadingModal";
import OnboardingCostsModal from "../../components/home/OnboardingCostsModal";
import ComingSoonModal from "../../components/ComingSoonModal";
import IntegrationRequiredState from "../../components/IntegrationRequiredState";
import ReportsPage from "../ReportsPage";
import ChatPage from "../ChatPage";
import {
  ASSESSMENT_SUMMARY_ITEM_BY_ID,
  buildAssessmentHeaderMeta,
} from "../../utils/assessmentSummaryConfig";
import {
  getChannelChatContextOptions,
  attachChannelChatContext,
  removeChannelChatContext,
} from "../../utils/api/channelChatApi";
import {
  readDraggedChatContext,
  sanitizeChatContext,
  setChatContextPickMode,
} from "../../utils/chatContextDrag";
import { usePageHeader } from "../../contexts/PageHeaderContext";

const buildViewerWindowChatContext = (windowInfo = null, baseKey = "assessment") => {
  if (!windowInfo) return null;
  const key =
    windowInfo.windowKey ||
    windowInfo.key ||
    windowInfo.label ||
    [windowInfo.startDate, windowInfo.endDate].filter(Boolean).join("_") ||
    "";
  if (!key) return null;
  const label =
    windowInfo.label ||
    (windowInfo.startDate && windowInfo.endDate
      ? `${windowInfo.startDate} to ${windowInfo.endDate}`
      : key);
  return {
    key: `report-window:${baseKey}:${key}`,
    label: `Window: ${label}`,
    type: "report-window",
    windowKey: key,
    windowLabel: label,
    window: {
      key,
      label,
      days: windowInfo.days ?? null,
      startDate: windowInfo.startDate || null,
      endDate: windowInfo.endDate || null,
      assessmentLabel: windowInfo.assessmentLabel || null,
    },
  };
};
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./HomePage.module.css";

const LOGIN_BOOT_KEYS = new Set();

const BOOTSTRAP_SESSION_KEY = "zeqi:homeBootstrapped";
const ACTIVE_CHANNEL_STORAGE_KEY = "zeqi:activeChannelId";
const ACTIVE_SUBCHANNEL_STORAGE_KEY = "zeqi:activeSubchannelId";
const DRAG_START_MOUSE_KEY = "__zeqiHomeAssessmentStartMouseDrag";
const DRAG_START_TOUCH_KEY = "__zeqiHomeAssessmentStartTouchDrag";
const getBootstrapSession = (userId) => {
  if (!userId) return false;
  try {
    const raw = window.sessionStorage.getItem(BOOTSTRAP_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.userId === userId && parsed?.bootstrapped === true;
  } catch {
    return false;
  }
};
const setBootstrapSession = (userId) => {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(
      BOOTSTRAP_SESSION_KEY,
      JSON.stringify({ userId, bootstrapped: true }),
    );
  } catch {
    // ignore
  }
};

const getPendingOverviewTab = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem("zeqi:pendingTab");
  } catch {
    return null;
  }
};

const HomePage = ({ forcedTabKey = null }) => {
  const { user } = useAuthUser();
  const { setShopData, shopData } = useShop();
  const { t } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageHeader, clearPageHeader } = usePageHeader();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const skip = !authToken;
  const userId = user?.userId || user?._id || user?.id;
  const alreadyBootstrapped = getBootstrapSession(userId);

  const {
    data: shopResponse,
    loading: shopLoading,
    refetch: refetchShop,
  } = useQuery(GET_SHOP_DATA_FOR_RECOMMENDATION, {
    skip,
    context: { headers },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  // Migrated from deprecated onCompleted - use useEffect to handle side effects
  useEffect(() => {
    const shop = shopResponse?.getShopDataForRecommendation?.shop;
    if (shop) {
      setShopData(shop);
    }
  }, [shopResponse, setShopData]);

  const { data: progressData, refetch: refetchProgress } = useQuery(
    GET_PRIMARY_TASK_PROGRESS,
    {
      skip,
      context: { headers },
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    },
  );

  const { data: tokenData, refetch: refetchTokens } = useQuery(
    GET_USER_ACCOUNT_TOKENS,
    {
      skip,
      context: { headers },
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    },
  );

  const shop = shopResponse?.getShopDataForRecommendation?.shop;
  const hasCommerceConnection = Boolean(
    user?.shopifyToken ||
      shop?._id ||
      shop?.id ||
      shopData?._id ||
      shopData?.id ||
      shopData?.myshopifyDomain,
  );
  const shopSyncStatus = shop?.syncStatus || "idle";
  const isShopSyncing = shopSyncStatus === "syncing";
  const progress = progressData?.getPrimaryTaskProgress;
  const progressPercent = Number(progress?.percent) || 0;
  const tokens = tokenData?.getUserAccountTokens;
  const shopId = shop?.id || shop?._id;
  const tokensFetched = tokenData !== undefined;

  const [googleSyncReady, setGoogleSyncReady] = useState(false);
  const [facebookSyncReady, setFacebookSyncReady] = useState(false);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [shopDataReadiness, setShopDataReadiness] = useState(null);
  const [showLoadingModal, setShowLoadingModal] =
    useState(!alreadyBootstrapped);
  const [displayProgress, setDisplayProgress] = useState(
    alreadyBootstrapped ? 100 : 0,
  );
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const hasBootstrappedRef = useRef(alreadyBootstrapped);
  const hasShownOnboardingRef = useRef(false);
  const shopDataReadinessRef = useRef(null);
  const prevUserIdRef = useRef(null);
  const READINESS_CACHE_TTL_MS = 5 * 60 * 1000;

  const isGoogleConnected = Boolean(
    tokens?.googleToken ||
    tokens?.googleRefreshToken ||
    user?.googleToken ||
    user?.googleRefreshToken,
  );
  const isFacebookConnected = Boolean(
    tokens?.facebookToken || user?.facebookToken,
  );
  const assessmentTabLabel =
    t("ZeqiMainTab.assessment") ||
    t("HomePage.assessmentMatrix") ||
    "Assessment";
  const strategyTabLabel = t("ZeqiMainTab.strategy") || "Strategy";
  const reportsTabLabel = t("ZeqiMainTab.reports") || "Reports";
  const keyFocusTabLabel = t("ZeqiMainTab.keyFocus") || "Key Focus";
  const chatTabLabel = t("ZeqiMainTab.chat") || "Chat";
  const routeOverviewTab = location.state?.overviewTab || null;
  const assessmentSummaryFocusId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("summary") || "";
  }, [location.search]);
  const assessmentSummaryFocus = useMemo(
    () => ASSESSMENT_SUMMARY_ITEM_BY_ID[assessmentSummaryFocusId] || null,
    [assessmentSummaryFocusId],
  );

  // Sync page header whenever the active assessment changes.
  // The seed from navigation state gives us the best available timestamp
  // (sourced from the rail's reportByCollection data). buildAssessmentHeaderMeta
  // applies canonical title priority so the header is always correct on refresh too.
  useEffect(() => {
    if (!assessmentSummaryFocusId) {
      clearPageHeader();
      return undefined;
    }
    const seed = location.state?.assessmentHeaderSeed || null;
    const meta = buildAssessmentHeaderMeta({
      summaryId: assessmentSummaryFocusId,
      seededTitle: seed?.title || null,
      assessmentData: null,
      backingReport: seed?.latestUpdatedAt
        ? { updatedAt: seed.latestUpdatedAt }
        : null,
    });
    setPageHeader(meta);
    return () => clearPageHeader();
  }, [assessmentSummaryFocusId, location.state, setPageHeader, clearPageHeader]);

  const forcedTabLabel = useMemo(() => {
    switch (forcedTabKey) {
      case "assessment":
        return assessmentTabLabel;
      case "reports":
        return reportsTabLabel;
      case "strategy":
        return strategyTabLabel;
      case "keyFocus":
        return keyFocusTabLabel;
      case "chat":
        return chatTabLabel;
      default:
        return null;
    }
  }, [
    assessmentTabLabel,
    chatTabLabel,
    forcedTabKey,
    keyFocusTabLabel,
    reportsTabLabel,
    strategyTabLabel,
  ]);

  const [activeTab, setActiveTab] = useState(
    () => forcedTabLabel || routeOverviewTab || getPendingOverviewTab() || chatTabLabel,
  );
  const [tabPreferenceReady, setTabPreferenceReady] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState(null);
  const assessmentSplitRef = useRef(null);
  const [assessmentTopFlex, setAssessmentTopFlex] = useState(0.58);
  const [assessmentChatPaneState, setAssessmentChatPaneState] = useState("default");
  const [assessmentViewerWindow, setAssessmentViewerWindow] = useState(null);
  const isAssessmentChatClosed = assessmentChatPaneState === "minimized";
  const handleExpandAssessmentChat = useCallback(() => {
    setAssessmentChatPaneState((current) => {
      if (current === "minimized") return "default";
      if (current === "default") return "maximized";
      return "default";
    });
  }, []);
  const handleExpandAssessmentReports = useCallback(() => {
    setAssessmentChatPaneState((current) => {
      if (current === "maximized") return "default";
      if (current === "default") return "minimized";
      return "minimized";
    });
    setAssessmentTopFlex((current) => Math.max(current, 0.72));
  }, []);
  const [activeChannelId, setActiveChannelId] = useState(() => {
    try {
      return window.sessionStorage.getItem(ACTIVE_CHANNEL_STORAGE_KEY) || "main";
    } catch {
      return "main";
    }
  });
  const [activeSubchannelId, setActiveSubchannelId] = useState(() => {
    try {
      return (
        window.sessionStorage.getItem(ACTIVE_SUBCHANNEL_STORAGE_KEY) || ""
      );
    } catch {
      return "";
    }
  });
  const tabLabelsRef = useRef({
    assessment: assessmentTabLabel,
    strategy: strategyTabLabel,
    reports: reportsTabLabel,
    keyFocus: keyFocusTabLabel,
    chat: chatTabLabel,
  });

  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 900px)").matches,
  );
  const [headerSlotEl, setHeaderSlotEl] = useState(null);
  const [channelContextStateById, setChannelContextStateById] = useState({});
  const assessmentContextMenuRef = useRef(null);
  const assessmentChatSessionIdRef = useRef(null);
  const [assessmentDismissedContextKeys, setAssessmentDismissedContextKeys] = useState([]);
  const [assessmentExtraChatContexts, setAssessmentExtraChatContexts] = useState([]);
  const [assessmentContextOptions, setAssessmentContextOptions] = useState([]);
  const [assessmentContextMenuOpen, setAssessmentContextMenuOpen] = useState(false);
  const [isAssessmentContextDropActive, setIsAssessmentContextDropActive] = useState(false);

  const assessmentBaseChatContext = useMemo(
    () => ({
      key: assessmentSummaryFocus?.id || "assessment",
      label: assessmentSummaryFocus?.label || "Assessment",
      type: "assessment",
    }),
    [assessmentSummaryFocus],
  );
  const assessmentChatContextTrail = useMemo(() => {
    const next = [assessmentBaseChatContext];
    const windowContext = buildViewerWindowChatContext(
      assessmentViewerWindow,
      assessmentBaseChatContext.key,
    );
    if (windowContext) next.push(windowContext);
    assessmentExtraChatContexts.forEach((entry) => {
      if (!entry?.key) return;
      if (!next.some((existing) => existing.key === entry.key)) {
        next.push(entry);
      }
    });
    return next;
  }, [assessmentBaseChatContext, assessmentExtraChatContexts, assessmentViewerWindow]);
  const visibleAssessmentChatContextTrail = useMemo(() => {
    const next = assessmentChatContextTrail.filter(
      (entry) => !assessmentDismissedContextKeys.includes(entry.key)
    );
    return next.length ? next : [assessmentBaseChatContext];
  }, [
    assessmentBaseChatContext,
    assessmentChatContextTrail,
    assessmentDismissedContextKeys,
  ]);
  const activeAssessmentChatContext = useMemo(
    () => sanitizeChatContext(visibleAssessmentChatContextTrail) || assessmentBaseChatContext,
    [assessmentBaseChatContext, visibleAssessmentChatContextTrail],
  );
  const availableAssessmentContextOptions = useMemo(
    () =>
      assessmentContextOptions.filter(
        (entry) =>
          entry?.key &&
          entry.key !== activeAssessmentChatContext?.key &&
          !visibleAssessmentChatContextTrail.some(
            (context) => context.key === entry.key
          )
      ),
    [
      activeAssessmentChatContext?.key,
      assessmentContextOptions,
      visibleAssessmentChatContextTrail,
    ]
  );

  const addAssessmentChatContext = useCallback((nextContext, { silent = false } = {}) => {
    if (!nextContext?.key) return;
    setAssessmentDismissedContextKeys((current) =>
      current.filter((entry) => entry !== nextContext.key)
    );
    setAssessmentExtraChatContexts((current) => {
      const replaceSingletonType =
        nextContext.type === "pdp-product" || nextContext.type === "report-window";
      const remaining = current.filter((entry) => {
        if (entry?.key === nextContext.key) return false;
        if (replaceSingletonType && entry?.type === nextContext.type) return false;
        return true;
      });
      return [...remaining, nextContext];
    });
    if (!silent && nextContext?.label) {
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-context-notification", {
          detail: { type: "added", label: nextContext.label, key: nextContext.key },
        }),
      );
    }
    const sessionId = assessmentChatSessionIdRef.current;
    if (sessionId && userId && authToken) {
      attachChannelChatContext({
        userId,
        sessionId,
        channelId: "main",
        context: nextContext,
        authToken,
      }).catch((err) => {
        console.warn("[homepage] attach context failed", err?.message || err);
      });
    }
  }, [userId, authToken]);

  const handleAssessmentContextDrop = useCallback(
    (event) => {
      event.preventDefault();
      const droppedContext = readDraggedChatContext(event);
      setIsAssessmentContextDropActive(false);
      if (!droppedContext?.key) return;
      addAssessmentChatContext(droppedContext);
    },
    [addAssessmentChatContext],
  );

  const dismissAssessmentChatContext = useCallback(
    (contextKey) => {
      if (!contextKey || contextKey === assessmentBaseChatContext.key) return;
      setAssessmentDismissedContextKeys((current) =>
        current.includes(contextKey) ? current : [...current, contextKey]
      );
      setAssessmentExtraChatContexts((current) => {
        const item = current.find((c) => c?.key === contextKey);
        if (item?.label) {
          window.dispatchEvent(
            new CustomEvent("zeqi:chat-context-notification", {
              detail: { type: "removed", label: item.label, key: contextKey },
            }),
          );
        }
        return current.filter((c) => c?.key !== contextKey);
      });
      const sessionId = assessmentChatSessionIdRef.current;
      if (sessionId && userId && authToken) {
        removeChannelChatContext({
          userId,
          sessionId,
          contextKey,
          authToken,
        }).catch((err) => {
          console.warn("[homepage] remove context failed", err?.message || err);
        });
      }
    },
    [assessmentBaseChatContext.key, userId, authToken],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleAutoChatContext = (event) => {
      const { channelId, context } = event?.detail || {};
      if (!context?.key) return;
      const matchesContext =
        !channelId ||
        channelId === "pdp" ||
        context.collectionName === "conversion_and_pdp_report";
      console.log("[HomePage:autoContext] received zeqi:add-chat-context", { channelId, contextKey: context.key, contextType: context.type, label: context.label, matchesContext });
      if (!matchesContext) return;
      addAssessmentChatContext(context, { silent: true });
    };
    window.addEventListener("zeqi:add-chat-context", handleAutoChatContext);
    return () => {
      window.removeEventListener("zeqi:add-chat-context", handleAutoChatContext);
    };
  }, [addAssessmentChatContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleRemoveChatContext = (event) => {
      const { key, type } = event?.detail || {};
      console.log("[HomePage:removeContext] received zeqi:remove-chat-context", { key, type });
      setAssessmentExtraChatContexts((current) => {
        if (key) return current.filter((c) => c?.key !== key);
        if (type) return current.filter((c) => c?.type !== type);
        return current;
      });
    };
    window.addEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    return () => {
      window.removeEventListener("zeqi:remove-chat-context", handleRemoveChatContext);
    };
  }, []);

  const handleAssessmentSessionContextLoaded = useCallback((trail, detachedKeys) => {
    if (Array.isArray(detachedKeys) && detachedKeys.length) {
      setAssessmentDismissedContextKeys(detachedKeys);
    }
    if (Array.isArray(trail) && trail.length) {
      const baseKey = assessmentBaseChatContext.key;
      const extraItems = trail.filter((item) => item?.key && item.key !== baseKey);
      setAssessmentExtraChatContexts(extraItems.length ? extraItems : []);
    }
  }, [assessmentBaseChatContext.key]);

  useEffect(() => {
    assessmentChatSessionIdRef.current = null;
    setAssessmentDismissedContextKeys([]);
    setAssessmentExtraChatContexts([]);
    setAssessmentContextMenuOpen(false);
    setAssessmentViewerWindow(null);
  }, [assessmentSummaryFocusId]);

  const handleAssessmentSessionIdReady = useCallback((sid) => {
    if (sid) {
      assessmentChatSessionIdRef.current = sid;
      console.log("[HomePage] assessmentChatSessionIdRef set", { sessionId: sid });
    }
  }, []);

  useEffect(() => {
    setChatContextPickMode(assessmentContextMenuOpen);
    return () => setChatContextPickMode(false);
  }, [assessmentContextMenuOpen]);

  useEffect(() => {
    let active = true;
    if (!userId || !authToken || !shopId) return undefined;

    getChannelChatContextOptions({
      userId,
      shopId,
      scope: "assessment",
      summaryId: assessmentSummaryFocusId || null,
      authToken,
    })
      .then((response) => {
        if (!active) return;
        setAssessmentContextOptions(
          Array.isArray(response?.options) ? response.options : []
        );
      })
      .catch(() => {
        if (!active) return;
        setAssessmentContextOptions([]);
      });

    return () => {
      active = false;
    };
  }, [assessmentSummaryFocusId, authToken, shopId, userId]);

  useEffect(() => {
    if (!assessmentContextMenuOpen) return undefined;

    // Use click/touchend (not mousedown/touchstart) so that dragging a right-rail
    // item — which begins with mousedown — does NOT immediately close the menu
    // and kill pick mode before the drag can start.
    const handleClickOutside = (event) => {
      if (assessmentContextMenuRef.current?.contains(event.target)) return;
      setAssessmentContextMenuOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, [assessmentContextMenuOpen]);

  const startAssessmentResize = useCallback((startY) => {
    const shell = assessmentSplitRef.current;
    if (!shell) return;

    const shellRect = shell.getBoundingClientRect();
    const shellHeight = shellRect.height || 1;
    const initialTopHeight = shellHeight * assessmentTopFlex;

    const updateFromPointer = (clientY) => {
      const delta = clientY - startY;
      const nextTopHeight = Math.min(
        Math.max(initialTopHeight + delta, 140),
        shellHeight - 120,
      );
      setAssessmentTopFlex(nextTopHeight / shellHeight);
      setAssessmentChatPaneState("default");
    };

    const stopMouseDrag = () => {
      window.removeEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
      window.removeEventListener("mouseup", stopMouseDrag);
      delete window[DRAG_START_MOUSE_KEY];
    };

    const stopTouchDrag = () => {
      window.removeEventListener("touchmove", window[DRAG_START_TOUCH_KEY]);
      window.removeEventListener("touchend", stopTouchDrag);
      delete window[DRAG_START_TOUCH_KEY];
    };

    window[DRAG_START_MOUSE_KEY] = (event) => updateFromPointer(event.clientY);
    window[DRAG_START_TOUCH_KEY] = (event) => {
      const touch = event.touches[0];
      if (touch) updateFromPointer(touch.clientY);
    };

    window.addEventListener("mousemove", window[DRAG_START_MOUSE_KEY]);
    window.addEventListener("mouseup", stopMouseDrag);
    window.addEventListener("touchmove", window[DRAG_START_TOUCH_KEY], { passive: true });
    window.addEventListener("touchend", stopTouchDrag, { passive: true });
  }, [assessmentTopFlex]);

  const assessmentTopPaneStyle =
    assessmentChatPaneState === "maximized"
      ? { flex: "0 0 0%", minHeight: 0 }
      : isAssessmentChatClosed
        ? { flex: "1 1 0%", minHeight: 0 }
        : { flex: `${assessmentTopFlex} 1 0%` };
  const assessmentChatPaneStyle =
    assessmentChatPaneState === "minimized"
      ? { flex: "0 0 54px" }
      : assessmentChatPaneState === "maximized"
        ? { flex: "1 1 0%" }
        : { flex: `${1 - assessmentTopFlex} 1 0%` };

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const el = document.getElementById("header-tabs-slot");
    setHeaderSlotEl(el);
  }, []);

  useEffect(() => {
    if (forcedTabLabel) return undefined;
    const handleOverviewReclick = () => {
      setActiveTab(chatTabLabel);
    };
    window.addEventListener("zeqi:overview-reclick", handleOverviewReclick);
    return () => {
      window.removeEventListener("zeqi:overview-reclick", handleOverviewReclick);
    };
  }, [chatTabLabel, forcedTabLabel]);

  useEffect(() => {
    if (forcedTabLabel) return undefined;
    const handleSwitchTab = (e) => {
      const tab = e?.detail?.tab;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener("zeqi:switch-tab", handleSwitchTab);
    return () => window.removeEventListener("zeqi:switch-tab", handleSwitchTab);
  }, [forcedTabLabel]);

  useEffect(() => {
    const handleTourOpenAssessment = () => {
      setActiveTab(assessmentTabLabel);
      setAssessmentChatPaneState("default");
      setAssessmentTopFlex(0.58);
    };
    window.addEventListener("zeqi:tour-open-assessment", handleTourOpenAssessment);
    return () =>
      window.removeEventListener(
        "zeqi:tour-open-assessment",
        handleTourOpenAssessment,
      );
  }, [assessmentTabLabel]);

  useEffect(() => {
    if (forcedTabLabel) return undefined;
    const handleChannelChanged = (event) => {
      const channelId = event?.detail?.channelId || "main";
      const subchannelId = event?.detail?.subchannelId || "";
      setActiveChannelId(channelId);
      setActiveSubchannelId(subchannelId);
    };
    window.addEventListener("zeqi:channel-changed", handleChannelChanged);
    return () =>
      window.removeEventListener("zeqi:channel-changed", handleChannelChanged);
  }, [forcedTabLabel]);

  // Always default Overview to Chat when entering this page,
  // unless a pending tab was set by the right-rail nav buttons.
  useLayoutEffect(() => {
    if (!userId) {
      setTabPreferenceReady(false);
      return;
    }
    if (forcedTabLabel) {
      setActiveTab(forcedTabLabel);
      setTabPreferenceReady(true);
      return;
    }
    const pending = routeOverviewTab || getPendingOverviewTab();
    if (pending) {
      window.sessionStorage.removeItem("zeqi:pendingTab");
      setActiveTab(pending);
    } else {
      setActiveTab(chatTabLabel);
    }
    setTabPreferenceReady(true);
  }, [
    forcedTabLabel,
    userId,
    chatTabLabel,
    routeOverviewTab,
  ]);

  // Save active tab to localStorage when it changes + broadcast to AppShell
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("zeqi:tab-changed", { detail: { tab: activeTab, isPostOnboarding } }));
    if (!userId || !tabPreferenceReady) return;
    try {
      localStorage.setItem(`zeqi:activeTab:${userId}`, activeTab);
    } catch {
      // ignore storage errors
    }
  }, [activeTab, userId, tabPreferenceReady]);

  useEffect(() => {
    const uid = (user?._id ?? user?.id ?? "").trim();
    if (!uid) {
      prevUserIdRef.current = null;
      return;
    }
    if (prevUserIdRef.current && prevUserIdRef.current === uid) {
      return;
    }
    prevUserIdRef.current = uid;
    LOGIN_BOOT_KEYS.clear();
    shopDataReadinessRef.current = null;
    setGoogleSyncReady(false);
    setFacebookSyncReady(false);
  }, [user?._id, user?.id]);

  useEffect(() => {
    const prev = tabLabelsRef.current;
    if (
      activeTab === prev.assessment &&
      assessmentTabLabel !== prev.assessment
    ) {
      setActiveTab(assessmentTabLabel);
    } else if (
      activeTab === prev.strategy &&
      strategyTabLabel !== prev.strategy
    ) {
      setActiveTab(strategyTabLabel);
    } else if (activeTab === prev.reports && reportsTabLabel !== prev.reports) {
      setActiveTab(reportsTabLabel);
    } else if (
      activeTab === prev.keyFocus &&
      keyFocusTabLabel !== prev.keyFocus
    ) {
      setActiveTab(keyFocusTabLabel);
    }
    tabLabelsRef.current = {
      assessment: assessmentTabLabel,
      strategy: strategyTabLabel,
      reports: reportsTabLabel,
      keyFocus: keyFocusTabLabel,
    };
  }, [
    activeTab,
    assessmentTabLabel,
    strategyTabLabel,
    reportsTabLabel,
    keyFocusTabLabel,
  ]);

  const runShopDataChecker = useCallback(
    async ({ force = false } = {}) => {
      if (!authToken || !shopId || !userId) return null;
      if (!force && shopDataReadinessRef.current) {
        return shopDataReadinessRef.current;
      }
      setTokensLoading(true);
      try {
        const readiness = await getShopDataReadiness({
          shopId,
          userId,
          authToken,
        });
        shopDataReadinessRef.current = readiness;
        setShopDataReadiness(readiness);
        try {
          window.sessionStorage.setItem(
            `zeqi:shopReadiness:${userId}`,
            JSON.stringify({ at: Date.now(), readiness }),
          );
        } catch {
          // ignore
        }
        return readiness;
      } catch {
        return null;
      } finally {
        setTokensLoading(false);
      }
    },
    [authToken, shopId, userId],
  );

  const refreshReadiness = useCallback(async () => {
    await runShopDataChecker({ force: true });
  }, [runShopDataChecker]);

  useEffect(() => {
    if (!userId) return;
    let usedCache = false;
    try {
      const raw = window.sessionStorage.getItem(`zeqi:shopReadiness:${userId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed?.at || 0);
        if (parsed?.readiness && age < READINESS_CACHE_TTL_MS) {
          shopDataReadinessRef.current = parsed.readiness;
          setShopDataReadiness(parsed.readiness);
          usedCache = true;
        }
      }
    } catch {
      // ignore
    }
    if (!usedCache) {
      refreshReadiness();
    }
  }, [userId, refreshReadiness, READINESS_CACHE_TTL_MS]);

  const readinessReady =
    typeof shopDataReadiness?.ready === "boolean"
      ? shopDataReadiness.ready
      : null;
  const shouldShowOnboarding =
    readinessReady === false &&
    googleSyncReady &&
    facebookSyncReady &&
    !tokensLoading &&
    Boolean(shop);

  useEffect(() => {
    if (shouldShowOnboarding && !hasShownOnboardingRef.current) {
      hasShownOnboardingRef.current = true;
      setShowOnboardingModal(true);
    }
    if (readinessReady === true && showOnboardingModal) {
      setShowOnboardingModal(false);
    }
  }, [shouldShowOnboarding, readinessReady, showOnboardingModal]);

  useEffect(() => {
    if (!isGoogleConnected) {
      setGoogleSyncReady(false);
      return;
    }
    setGoogleSyncReady(true);
  }, [isGoogleConnected]);

  useEffect(() => {
    if (!isFacebookConnected) {
      setFacebookSyncReady(false);
      return;
    }
    setFacebookSyncReady(true);
  }, [isFacebookConnected]);

  useEffect(() => {
    if (!authToken) return;
    if (!googleSyncReady || !facebookSyncReady) return;
    runShopDataChecker({ force: !shopDataReadinessRef.current }).catch(
      () => {},
    );
  }, [authToken, googleSyncReady, facebookSyncReady, runShopDataChecker]);

  useEffect(() => {
    if (progress?.promoted) {
      refetchShop?.().catch(() => {});
      refetchProgress?.().catch(() => {});
    }
  }, [progress?.promoted, refetchShop, refetchProgress]);

  useEffect(() => {
    const uid = (user?._id ?? user?.id ?? "").trim();
    if (!authToken || !uid) return;
    if (LOGIN_BOOT_KEYS.has(uid)) return;
    LOGIN_BOOT_KEYS.add(uid);
    (async () => {
      try {
        if (isGoogleConnected) {
          setGoogleSyncReady(true);
        }
        if (isFacebookConnected) {
          setFacebookSyncReady(true);
        }
        await refetchProgress?.().catch(() => {});
        await refreshReadiness?.().catch(() => {});
      } catch {
        LOGIN_BOOT_KEYS.delete(uid);
      }
    })();
  }, [
    authToken,
    user?._id,
    user?.id,
    isGoogleConnected,
    isFacebookConnected,
    refetchProgress,
    refreshReadiness,
  ]);

  // Main bootstrap effect - mirrors React Native behavior
  // When shop data loads successfully, set progress to 100 and close modal
  useEffect(() => {
    if (!hasCommerceConnection) {
      if (!hasBootstrappedRef.current) {
        hasBootstrappedRef.current = true;
        setBootstrapSession(userId);
      }
      setDisplayProgress(0);
      setShowLoadingModal(false);
      return;
    }

    if (!shop) {
      // If no shop data after 7 seconds (and not loading/no error), close modal
      if (authToken && !shopLoading && !shopResponse) {
        const timeout = setTimeout(() => {
          setDisplayProgress(0);
          setShowLoadingModal(false);
        }, 7000);
        return () => clearTimeout(timeout);
      }
      return;
    }

    // Shop data loaded successfully - set progress to 100 and close modal
    if (!hasBootstrappedRef.current) {
      hasBootstrappedRef.current = true;
      setBootstrapSession(userId);
      setDisplayProgress(100);
      if (!isShopSyncing) {
        setTimeout(() => setShowLoadingModal(false), 500);
      }
    }

    const uid = (user?._id ?? user?.id ?? "").trim();
    if (!uid || !authToken) return;
    (async () => {
      try {
        if (tokensFetched && isGoogleConnected) {
          setGoogleSyncReady(true);
        }
        if (tokensFetched && isFacebookConnected) {
          setFacebookSyncReady(true);
        }
      } catch {
        // ignore
      } finally {
        await refetchShop?.().catch(() => {});
        await refetchProgress?.().catch(() => {});
      }
    })();
  }, [
    shop,
    shopLoading,
    shopResponse,
    authToken,
    user?._id,
    user?.id,
    tokensFetched,
    isGoogleConnected,
    isFacebookConnected,
    refetchShop,
    refetchProgress,
    isShopSyncing,
    userId,
    hasCommerceConnection,
  ]);

  useEffect(() => {
    if (hasCommerceConnection && isShopSyncing) {
      setShowLoadingModal(true);
    }
  }, [hasCommerceConnection, isShopSyncing]);

  // Also close modal if GraphQL progress query returns 100
  useEffect(() => {
    if (progressPercent >= 100) {
      setDisplayProgress(100);
      setShowLoadingModal(false);
    } else if (
      progressPercent > displayProgress &&
      !hasBootstrappedRef.current
    ) {
      // Update display progress from GraphQL if it's higher (before bootstrap completes)
      setDisplayProgress(progressPercent);
    }
  }, [progressPercent, displayProgress]);

  useEffect(() => {
    if (!showLoadingModal) return;
    const id = window.setInterval(() => {
      refetchProgress?.().catch(() => {});
    }, 8000);
    return () => window.clearInterval(id);
  }, [showLoadingModal, refetchProgress]);

  const triggerWebhooks = useCallback(async () => {
    const uid = (user?._id ?? user?.id ?? "").trim();
    if (!shop || !uid || !authToken) return;
    try {
      await refetchShop?.().catch(() => {});
      await refetchProgress?.().catch(() => {});
      await refreshReadiness?.().catch(() => {});
    } catch {
      // ignore
    }
  }, [
    authToken,
    refetchProgress,
    refetchShop,
    refreshReadiness,
    shop,
    user?._id,
    user?.id,
  ]);

  const promotedRaw = progress?.promoted;
  const isPromoted =
    promotedRaw === true ||
    promotedRaw === "true" ||
    promotedRaw === 1 ||
    promotedRaw === "1";

  // Mirror the same cluster logic used in ZeqiStrategyReportSection to decide
  // when "Summary / Foundational / Business Reality Analysis" is shown.
  // progress.cluster_flag matches the primary strategy's cluster_flag.
  // Post-onboarding = cluster has moved to "foundation" or "funnel", OR promoted flag is set.
  // Early onboarding = cluster is null/empty; we treat that as still in onboarding.
  const progressCluster = String(progress?.cluster_flag || "").toLowerCase().trim();
  const isPostOnboarding =
    progressCluster.includes("foundation") ||
    progressCluster.includes("funnel") ||
    isPromoted;
  const showChannelWorkspace = !forcedTabKey && location.pathname === "/";
  const effectiveChannelContextId = activeSubchannelId || activeChannelId;
  const currentChannelContextState =
    channelContextStateById[effectiveChannelContextId] || null;
  const channelContextOpen =
    currentChannelContextState?.open === true;
  const selectedChannelContextAction = currentChannelContextState?.action || null;
  const channelHeaderActions = useMemo(
    () => getChannelWorkspaceHeaderActions(effectiveChannelContextId),
    [effectiveChannelContextId],
  );

  if (!user) {
    return (
      <section className={pageStyles.page} data-page="home">
        <div className={pageStyles.emptyState}>
          <h2>{t("HomePage.signInTitle") || "Sign in to continue"}</h2>
          <p>
            {t("HomePage.signInPromptPrefix") || "Use"}{" "}
            <Link to="/login">{t("HomePage.loginCta") || "Login"}</Link>{" "}
            {t("HomePage.signInPromptSuffix") ||
              "to feed Zeqi with your store and unlock the same dashboards as the mobile app."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`${pageStyles.page} ${styles.wrapper}`}
      data-page="home"
    >
      <div className={styles.headerBlock}>
        {/* <p className={styles.eyebrow}>{shop?.primaryEmail || "Global"}</p> */}
        {/* <h2 className={styles.greeting}>
          {t("HomePage.greeting", { name: ownerName }) || `Hey ${ownerName},`}
        </h2> */}
        {/* <ZeqiGreetingSection /> */}
      </div>

      {shopLoading && (
        <p className={styles.loading}>
          {/* {t("HomePage.loadingShop") || "Loading shop details..."} */}
        </p>
      )}

      {showChannelWorkspace && isDesktop && headerSlotEl && channelHeaderActions.length > 0
        ? createPortal(
            <div className={styles.channelHeaderPills}>
              {channelHeaderActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={`${styles.channelHeaderPill} ${
                    channelContextOpen &&
                    selectedChannelContextAction?.type === action.type &&
                    ((action.type === "assessment" &&
                      selectedChannelContextAction?.label === action.label) ||
                      (action.type === "report" &&
                        selectedChannelContextAction?.key === action.key))
                      ? styles.channelHeaderPillActive
                      : ""
                  }`}
                  onClick={() => {
                    if (action.type === "assessment") {
                      setChannelContextStateById((prev) => {
                        const current = prev[effectiveChannelContextId] || null;
                        return {
                          ...prev,
                          [effectiveChannelContextId]: {
                            action,
                            open:
                              current?.action?.type === action.type &&
                              current?.action?.label === action.label
                                ? !current?.open
                                : true,
                          },
                        };
                      });
                      return;
                    }
                    if (action.type === "report") {
                      setChannelContextStateById((prev) => {
                        const current = prev[effectiveChannelContextId] || null;
                        return {
                          ...prev,
                          [effectiveChannelContextId]: {
                            action,
                            open:
                              current?.action?.type === action.type &&
                              current?.action?.key === action.key
                                ? !current?.open
                                : true,
                          },
                        };
                      });
                      return;
                    }
                    if (action.type === "channel") {
                      window.dispatchEvent(
                        new CustomEvent("zeqi:set-active-channel", {
                          detail: {
                            channelId: action.channelId,
                            subchannelId: action.subchannelId || "",
                          },
                        }),
                      );
                    }
                  }}
                >
                  <span>{action.label}</span>
                  <ArrowUpRight size={13} strokeWidth={2.1} aria-hidden="true" />
                </button>
              ))}
            </div>,
            headerSlotEl,
          )
        : null}

      {!showChannelWorkspace &&
        (() => {
        return (
          <>
            {isDesktop && (
              <div className={styles.tabsRow}>
                <div
                  id="assessment-switch-slot"
                  className={styles.assessmentSwitchSlot}
                />
              </div>
            )}
          </>
        );
      })()}

      <div
        key={showChannelWorkspace ? "channel-workspace" : activeTab}
        className={`${styles.contentWrap} ${styles.tabTransition}`}
        data-active-tab={
          showChannelWorkspace
            ? activeSubchannelId || activeChannelId
            : activeTab
        }
      >
        {showChannelWorkspace ? (
          <ChannelWorkspace
            activeChannelId={activeChannelId}
            activeSubchannelId={activeSubchannelId}
            isContextOpen={channelContextOpen}
            contextSelection={selectedChannelContextAction}
            onCloseContext={() => {
              setChannelContextStateById((prev) => ({
                ...prev,
                [effectiveChannelContextId]: {
                  ...(prev[effectiveChannelContextId] || {}),
                  open: false,
                },
              }));
            }}
          />
        ) : activeTab === assessmentTabLabel ? (
          !hasCommerceConnection ? (
            <IntegrationRequiredState
              eyebrow="Connect Shopify to unlock assessments"
              title="Zeqi needs your commerce data before assessments can light up."
              body="Connect your Shopify store in Integrations so I can interpret orders, products, revenue health, and conversion patterns with real business context."
              buttonLabel="Open integrations"
              onClick={() => navigate("/integrations")}
            />
          ) : (
          <div ref={assessmentSplitRef} className={styles.assessmentSplitShell}>
            <div
              className={`${styles.assessmentTopPane} ${
                assessmentChatPaneState === "maximized"
                  ? styles.assessmentTopPaneHidden
                  : ""
              }`}
              style={assessmentTopPaneStyle}
            >
              <ZeqiStrategyReportSection
                view="assessment"
                summaryFocusId={assessmentSummaryFocusId}
                onRefreshExtra={triggerWebhooks}
                assessmentSwitchTargetId="assessment-switch-slot"
                isGoogleConnected={isGoogleConnected}
                isFacebookConnected={isFacebookConnected}
                googleSyncReady={googleSyncReady}
                facebookSyncReady={facebookSyncReady}
                tokensLoading={tokensLoading}
                shopDataReadiness={shopDataReadiness}
                shopDataReadinessLoading={tokensLoading}
                refetchTokens={() => {
                  refreshReadiness();
                  return refetchTokens?.();
                }}
                onOpenProductCosts={() => navigate("/product-costs")}
                onOpenOperatingCosts={() => navigate("/operating-costs")}
                isPromoted={isPromoted}
                onWindowChange={setAssessmentViewerWindow}
              />
            </div>

            {!isAssessmentChatClosed ? <div
              className={styles.assessmentPaneDivider}
              aria-hidden="true"
              data-tour-id="chat-divider"
            >
              <button
                type="button"
                className={styles.assessmentDragHandle}
                onMouseDown={(event) => startAssessmentResize(event.clientY)}
                onTouchStart={(event) => {
                  const touch = event.touches[0];
                  if (touch) startAssessmentResize(touch.clientY);
                }}
                aria-label="Resize assessment chat"
              />
              <div className={styles.assessmentDividerControls}>
                <div className={styles.assessmentChatTitleWrap}>
                  <span className={styles.assessmentChatTitle}>Zeqi chat</span>
                  <div
                    className={`${styles.assessmentChatContextTrail} ${
                      isAssessmentContextDropActive
                        ? styles.assessmentChatContextTrailDropActive
                        : ""
                    }`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsAssessmentContextDropActive(true);
                    }}
                    onDragEnter={(event) => {
                      event.preventDefault();
                      setIsAssessmentContextDropActive(true);
                    }}
                    onDragLeave={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget)) return;
                      setIsAssessmentContextDropActive(false);
                    }}
                    onDrop={handleAssessmentContextDrop}
                  >
                    {visibleAssessmentChatContextTrail.map((context) => {
                      const isDismissible =
                        context.key !== assessmentBaseChatContext.key;
                      return (
                        <span
                          key={context.key}
                          className={styles.assessmentChatContextPill}
                        >
                          <span className={styles.assessmentChatContextPillText}>
                            {context.label}
                          </span>
                          {isDismissible ? (
                            <button
                              type="button"
                              className={styles.assessmentChatContextPillReset}
                              onClick={() => dismissAssessmentChatContext(context.key)}
                              aria-label={`Remove ${context.label} context`}
                              title={`Remove ${context.label} context`}
                            >
                              <X size={12} strokeWidth={2.2} aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      );
                    })}
                    <div
                      ref={assessmentContextMenuRef}
                      className={styles.assessmentContextMenuWrap}
                      onMouseDown={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={`${styles.assessmentChatContextAddButton} ${
                          assessmentContextMenuOpen
                            ? styles.assessmentChatContextAddButtonActive
                            : ""
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setAssessmentContextMenuOpen((current) => !current);
                        }}
                        aria-label="Toggle chat context pick mode"
                        title={
                          assessmentContextMenuOpen
                            ? "Exit chat context pick mode"
                            : "Enter chat context pick mode"
                        }
                      >
                        {assessmentContextMenuOpen
                          ? <X size={12} strokeWidth={2.2} />
                          : <Plus size={12} strokeWidth={2.2} />
                        }
                      </button>
                    </div>
                  </div>
                </div>
                <div className={styles.assessmentControlButtons}>
                  <button
                    type="button"
                    className={styles.assessmentControlButton}
                    onClick={handleExpandAssessmentChat}
                    aria-label="Expand assessment chat"
                  >
                    <ArrowUp size={16} strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    className={styles.assessmentControlButton}
                    onClick={handleExpandAssessmentReports}
                    aria-label="Restore assessment chat"
                  >
                    <ArrowDown size={16} strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    className={styles.assessmentControlButton}
                    onClick={() => setAssessmentChatPaneState("minimized")}
                    aria-label="Close assessment chat"
                  >
                    <X size={16} strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </div> : null}

            {!isAssessmentChatClosed ? <div
              className={`${styles.assessmentChatPane} ${
                assessmentChatPaneState === "minimized"
                  ? styles.assessmentChatPaneMinimized
                  : assessmentChatPaneState === "maximized"
                    ? styles.assessmentChatPaneMaximized
                    : ""
              }`}
              style={assessmentChatPaneStyle}
            >
              <div className={styles.assessmentChatBody}>
                <ChatPage
                  inline
                  variant="inline"
                  channelId="main"
                  chatContext={activeAssessmentChatContext}
                  chatContextTrail={visibleAssessmentChatContextTrail}
                  onSessionContextLoaded={handleAssessmentSessionContextLoaded}
                  onSessionIdReady={handleAssessmentSessionIdReady}
                  inputPlaceholder={`Ask about ${
                    activeAssessmentChatContext?.label || "assessment"
                  }...`}
                  viewerTimeWindow={assessmentViewerWindow}
                />
              </div>
            </div> : null}
            {isAssessmentChatClosed ? (
              <div className={styles.assessmentChatReopenDock}>
                <button
                  type="button"
                  className={styles.assessmentChatReopenField}
                  onClick={() => setAssessmentChatPaneState("default")}
                  aria-label="Reopen Zeqi chat"
                  title="Reopen Zeqi chat"
                >
                  <span className={styles.assessmentChatReopenFieldIcon}>
                    <Plus size={18} strokeWidth={1.9} />
                  </span>
                  <span className={styles.assessmentChatReopenFieldText}>
                    Ask about assessment...
                  </span>
                  <span className={styles.assessmentChatReopenFieldSend}>
                    <Send size={16} strokeWidth={1.9} />
                  </span>
                </button>
              </div>
            ) : null}
          </div>
          )
        ) : activeTab === strategyTabLabel ? (
          <ZeqiStrategyReportSection
            view="strategy"
            onRefreshExtra={triggerWebhooks}
            assessmentSwitchTargetId="assessment-switch-slot"
            isGoogleConnected={isGoogleConnected}
            isFacebookConnected={isFacebookConnected}
            googleSyncReady={googleSyncReady}
            facebookSyncReady={facebookSyncReady}
            tokensLoading={tokensLoading}
            shopDataReadiness={shopDataReadiness}
            shopDataReadinessLoading={tokensLoading}
            refetchTokens={() => {
              refreshReadiness();
              return refetchTokens?.();
            }}
            onOpenProductCosts={() => navigate("/product-costs")}
            onOpenOperatingCosts={() => navigate("/operating-costs")}
            isPromoted={isPromoted}
          />
        ) : activeTab === reportsTabLabel ? (
          !hasCommerceConnection ? (
            <IntegrationRequiredState
              eyebrow="Connect Shopify to unlock reports"
              title="Zeqi needs your commerce data before reports and assessments can light up."
              body="Connect your Shopify store in Integrations so I can analyze orders, products, revenue, customer behavior, and conversion patterns."
              buttonLabel="Open integrations"
              onClick={() => navigate("/integrations")}
            />
          ) : (
            <ReportsPage />
          )
        ) : activeTab === chatTabLabel ? (
          <div className={styles.chatTabContent}>
            <ChatPage inline variant="inline" />
          </div>
        ) : (
          <ZeqiKeyFocusAreaSection
            userId={userId}
            isGoogleConnected={isGoogleConnected}
            isFacebookConnected={isFacebookConnected}
            googleSyncReady={googleSyncReady}
            facebookSyncReady={facebookSyncReady}
            tokensLoading={tokensLoading}
            shopDataReadiness={shopDataReadiness}
            shopDataReadinessLoading={tokensLoading}
            refetchTokens={() => {
              refreshReadiness();
              return refetchTokens?.();
            }}
            onOpenProductCosts={() => navigate("/product-costs")}
            onOpenOperatingCosts={() => navigate("/operating-costs")}
          />
        )}
      </div>

      <ZeqiLoadingModal
        visible={showLoadingModal}
        progress={displayProgress}
        onClose={() => setShowLoadingModal(false)}
        status={
          isShopSyncing
            ? t("StrategyReportSection.connect.syncing") ||
              "Hang tight—we’re syncing your data now. This only takes a moment."
            : undefined
        }
      />

      <OnboardingCostsModal
        visible={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        onOpenProduct={() => navigate("/product-costs")}
        onOpenOperating={() => navigate("/operating-costs")}
      />

      {comingSoonFeature && (
        <ComingSoonModal
          feature={comingSoonFeature}
          onClose={() => setComingSoonFeature(null)}
        />
      )}
    </section>
  );
};

export default HomePage;
